<template>
  <div class="arrival-dashboard nx-page nx-report-page">
    <nx-report-header
      title="铁路到站量趋势分析"
      subtitle="实时监控铁路煤炭运输到站情況，优化采购和库存管理策略"
    />

    <el-form inline class="filter-bar" :model="query">
      <el-form-item label="时间范围">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="从"
          end-placeholder="至"
          value-format="yyyy-MM-dd"
          unlink-panels
        />
      </el-form-item>
      <el-form-item label="煤种">
        <el-select
          v-model="query.cargoDictId"
          filterable
          remote
          reserve-keyword
          clearable
          placeholder="拼音码/名称检索"
          :remote-method="remoteCargo"
          :loading="cargoLoading"
          style="min-width: 220px"
          @change="onCargoChange"
        >
          <el-option
            v-if="selectedCargo && !cargoList.some((c) => sameId(c.id, selectedCargo.id))"
            :key="'sel-' + selectedCargo.id"
            :label="cargoLabel(selectedCargo)"
            :value="selectedCargo.id"
          />
          <el-option
            v-for="c in cargoList"
            :key="c.id"
            :label="cargoLabel(c)"
            :value="c.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" icon="el-icon-search" @click="handleQuery">查询</el-button>
      </el-form-item>
    </el-form>

    <el-row :gutter="16" class="chart-row">
      <el-col :span="14">
        <el-card shadow="hover" class="chart-card">
          <div class="card-head">
            <span class="card-title">到站车厢数趋势</span>
            <el-radio-group v-model="sumType" size="small" @change="loadTrend">
              <el-radio-button label="0">日</el-radio-button>
              <el-radio-button label="1">周</el-radio-button>
              <el-radio-button label="2">月</el-radio-button>
            </el-radio-group>
          </div>
          <div ref="trendChart" class="echart-box"></div>
        </el-card>
      </el-col>
      <el-col :span="10">
        <el-card shadow="hover" class="chart-card">
          <div class="card-head">
            <span class="card-title">煤种到站量对比</span>
          </div>
          <div ref="barChart" class="echart-box"></div>
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="hover" class="table-card">
      <div class="card-head">
        <span class="card-title">到站明细数据</span>
        <el-button type="primary" size="small" icon="el-icon-download" @click="exportExcel">导出</el-button>
      </div>
      <el-table v-loading="tableLoading" :data="tableRecords" border stripe style="width: 100%">
        <el-table-column prop="trainArrivalTime" label="日期" width="170" :formatter="fmtDate" />
        <el-table-column prop="trainNumber" label="车次编号" width="140">
          <template slot-scope="{ row }">
            <span class="link-train">{{ row.trainNumber || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="cargoName" label="煤种" min-width="100" />
        <el-table-column prop="planTotalWeight" label="核定载量(吨)" width="120" align="right" :formatter="fmtNum" />
        <el-table-column prop="totalWeight" label="实载(吨)" width="100" align="right" :formatter="fmtNum" />
        <el-table-column prop="lossWeight" label="亏量(吨)" width="100" align="right" :formatter="fmtNum" />
      </el-table>
      <el-pagination
        class="pager"
        background
        layout="total, prev, pager, next"
        :total="tableTotal"
        :page-size="query.size"
        :current-page.sync="query.page"
        @current-change="loadTable"
      />
    </el-card>

    <div class="footer-stats">
      <el-card shadow="hover" class="stat-mini">
        <div class="stat-val">{{ summary.totalTrains }}</div>
        <div class="stat-label">总到站车厢数</div>
        <div class="stat-unit">节</div>
      </el-card>
      <el-card shadow="hover" class="stat-mini">
        <div class="stat-val">{{ formatTon(summary.totalWeight) }}</div>
        <div class="stat-label">总煤炭吨位</div>
        <div class="stat-unit">吨</div>
      </el-card>
      <el-card shadow="hover" class="stat-pie-card">
        <div class="stat-label pie-title">主要煤种占比</div>
        <div class="pie-legend">
          <div v-for="(item, idx) in summary.cargoPie" :key="item.name" class="pie-item">
            <i class="dot" :style="{ background: pieColors[idx % pieColors.length] }" />
            <span>{{ item.name }} {{ item.percent }}%</span>
          </div>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import {
  fetchArrivalTrend,
  fetchCargoBar,
  fetchArrivalDetailPage,
  fetchArrivalSummary,
  searchVardictPycode
} from './api'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'

function padRange (start, end) {
  return {
    startTime: start + ' 00:00:00',
    endTime: end + ' 23:59:59'
  }
}

export default {
  name: 'ArrivalAnalysis',
  components: { NxReportHeader },
  data () {
    const end = new Date()
    const start = new Date()
    start.setDate(end.getDate() - 6)
    const fmt = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    return {
      dateRange: [fmt(start), fmt(end)],
      query: {
        cargoDictId: null,
        page: 1,
        size: 10
      },
      sumType: '0',
      cargoList: [],
      cargoLoading: false,
      selectedCargo: null,
      tableRecords: [],
      tableTotal: 0,
      tableLoading: false,
      summary: {
        totalTrains: 0,
        totalWeight: 0,
        cargoPie: []
      },
      pieColors: ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399', '#9b59b6'],
      trendChart: null,
      barChart: null
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.trendChart = echarts.init(this.$refs.trendChart)
      this.barChart = echarts.init(this.$refs.barChart)
      window.addEventListener('resize', this.onResize)
      this.handleQuery()
    })
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.onResize)
    this.trendChart && this.trendChart.dispose()
    this.barChart && this.barChart.dispose()
  },
  methods: {
    buildPayload () {
      if (!this.dateRange || this.dateRange.length !== 2) {
        this.$message.warning('请选择时间范围')
        return null
      }
      const t = padRange(this.dateRange[0], this.dateRange[1])
      return {
        startTime: t.startTime,
        endTime: t.endTime,
        cargoNo: this.selectedCargo && this.selectedCargo.varNo ? this.selectedCargo.varNo : undefined,
        sumType: this.sumType
      }
    },
    async handleQuery () {
      const base = this.buildPayload()
      if (!base) return
      this.query.page = 1
      await Promise.all([this.loadTrend(), this.loadBar(), this.loadSummary(), this.loadTable()])
    },
    async remoteCargo (query) {
      this.cargoLoading = true
      try {
        const { data } = await searchVardictPycode(query)
        const raw = Array.isArray(data) ? data : data && data.data
        this.cargoList = Array.isArray(raw) ? raw : []
      } catch (e) {
        this.cargoList = []
      } finally {
        this.cargoLoading = false
      }
    },
    cargoLabel (row) {
      if (!row) return ''
      return row.cargoName || row.varName || ''
    },
    sameId (a, b) {
      return a != null && b != null && String(a) === String(b)
    },
    onCargoChange (val) {
      if (val == null || val === '') {
        this.selectedCargo = null
        return
      }
      const row = this.cargoList.find((c) => this.sameId(c.id, val))
      if (row) {
        this.selectedCargo = {
          id: row.id,
          varNo: row.varNo,
          varName: row.varName,
          cargoName: row.cargoName
        }
      }
    },
    async loadTrend () {
      const base = this.buildPayload()
      if (!base || !this.trendChart) return
      try {
        const { data } = await fetchArrivalTrend({ ...base, sumType: this.sumType })
        const d = data.data || {}
        const titles = d.title || []
        const carriageCounts = d.trainCount || d.data || []
        this.trendChart.setOption({
          color: ['#409EFF'],
          tooltip: { trigger: 'axis' },
          grid: { left: '3%', right: '4%', bottom: '3%', top: '18%', containLabel: true },
          xAxis: { type: 'category', data: titles, boundaryGap: false },
          yAxis: { type: 'value', name: '车厢数' },
          series: [
            {
              name: '到站车厢数',
              type: 'line',
              smooth: true,
              symbol: 'circle',
              symbolSize: 8,
              areaStyle: { color: 'rgba(64,158,255,0.12)' },
              data: carriageCounts
            }
          ]
        }, true)
      } catch (e) {
        this.$message.error('趋势图加载失败')
      }
    },
    async loadBar () {
      const base = this.buildPayload()
      if (!base || !this.barChart) return
      try {
        const { data } = await fetchCargoBar(base)
        const d = data.data || {}
        const titles = d.title || []
        const vals = (d.data || []).map((v) => Number(v) || 0)
        this.barChart.setOption({
          tooltip: { trigger: 'axis' },
          grid: { left: '3%', right: '4%', bottom: '3%', top: '14%', containLabel: true },
          xAxis: { type: 'category', data: titles, axisLabel: { rotate: titles.length > 5 ? 30 : 0 } },
          yAxis: { type: 'value', name: '到站量(吨)' },
          series: [
            {
              type: 'bar',
              data: vals.map((v, i) => ({
                value: v,
                itemStyle: { color: this.pieColors[i % this.pieColors.length] }
              })),
              barMaxWidth: 48
            }
          ]
        }, true)
      } catch (e) {
        this.$message.error('柱状图加载失败')
      }
    },
    async loadSummary () {
      const base = this.buildPayload()
      if (!base) return
      try {
        const { data } = await fetchArrivalSummary(base)
        const s = data.data || {}
        this.summary = {
          totalTrains: s.totalTrains || 0,
          totalWeight: s.totalWeight || 0,
          cargoPie: s.cargoPie || []
        }
      } catch (e) {
        this.summary = { totalTrains: 0, totalWeight: 0, cargoPie: [] }
      }
    },
    async loadTable () {
      const base = this.buildPayload()
      if (!base) return
      this.tableLoading = true
      try {
        const { data } = await fetchArrivalDetailPage({
          ...base,
          page: this.query.page,
          size: this.query.size
        })
        const page = data.data || {}
        this.tableRecords = page.records || []
        this.tableTotal = page.total || 0
      } catch (e) {
        this.tableRecords = []
        this.tableTotal = 0
      } finally {
        this.tableLoading = false
      }
    },
    onResize () {
      this.trendChart && this.trendChart.resize()
      this.barChart && this.barChart.resize()
    },
    fmtDate (row, col, val) {
      if (!val) return '-'
      const s = String(val)
      return s.length >= 10 ? s.slice(0, 10) : s
    },
    fmtNum (row, col, val) {
      if (val === null || val === undefined || val === '') return '-'
      return Number(val).toFixed(2)
    },
    formatTon (v) {
      if (v === null || v === undefined) return '0'
      const n = Number(v)
      return n.toLocaleString('zh-CN', { maximumFractionDigits: 0 })
    },
    async exportExcel () {
      const base = this.buildPayload()
      if (!base) return
      const maxRows = 2000
      const size = Math.min(this.tableTotal || maxRows, maxRows)
      if (size < 1) {
        this.$message.warning('无数据可导出')
        return
      }
      try {
        const { data } = await fetchArrivalDetailPage({ ...base, page: 1, size })
        const list = (data.data && data.data.records) || []
        const { export_json_to_excel } = require('@/util/vendor/Export2Excel')
        const th = ['日期', '车次编号', '煤种', '核定载量(吨)', '实载(吨)', '亏量(吨)']
        const rows = list.map((r) => [
          this.fmtDate(null, null, r.trainArrivalTime),
          r.trainNumber || '',
          r.cargoName || '',
          r.planTotalWeight != null ? Number(r.planTotalWeight).toFixed(2) : '',
          r.totalWeight != null ? Number(r.totalWeight).toFixed(2) : '',
          r.lossWeight != null ? Number(r.lossWeight).toFixed(2) : ''
        ])
        export_json_to_excel(th, rows, '到站明細')
        if (this.tableTotal > maxRows) {
          this.$message.warning(`导出前 ${maxRows} 条，共 ${this.tableTotal} 条`)
        }
      } catch (e) {
        this.$message.error('导出失败')
      }
    }
  }
}
</script>

<style scoped>
.arrival-dashboard {
  padding: 12px 16px 24px;
  background: #fff;
}
.page-head {
  margin-bottom: 8px;
}
.page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}
.page-desc {
  margin: 6px 0 0;
  font-size: 13px;
  color: #909399;
}
.filter-bar {
  margin-bottom: 16px;
}
.chart-row {
  margin-bottom: 16px;
}
.chart-card {
  min-height: 360px;
}
.card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.card-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}
.echart-box {
  width: 100%;
  height: 300px;
}
.table-card {
  margin-bottom: 16px;
}
.pager {
  margin-top: 12px;
  text-align: right;
}
.link-train {
  color: #409eff;
  cursor: pointer;
}
.footer-stats {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: stretch;
}
.stat-mini {
  flex: 1;
  min-width: 160px;
  text-align: center;
  padding: 12px 8px;
}
.stat-val {
  font-size: 26px;
  font-weight: bold;
  color: #303133;
}
.stat-label {
  font-size: 13px;
  color: #909399;
  margin-top: 4px;
}
.stat-unit {
  font-size: 12px;
  color: #c0c4cc;
}
.stat-pie-card {
  flex: 1.2;
  min-width: 280px;
  padding: 12px 16px;
}
.pie-title {
  margin-bottom: 10px;
  font-weight: 600;
  color: #303133;
}
.pie-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
}
.pie-item {
  font-size: 13px;
  color: #606266;
  display: flex;
  align-items: center;
  gap: 6px;
}
.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}
</style>
